import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OrganizationMembershipService } from '../../core/data-services/organization-membership.service';
import { calculateBitwiseRole, validateRoleChange } from '../../core/utils/member-role-utils';
import { MembershipRoles } from '../../core/data-models/membership-roles.model';
import { AdminMemberView } from '../../core/data-models/admin-member-view.model';

/**
 * Edit Member Role Modal Component
 *
 * Modal for editing member roles (Pilot and/or PIC bits only).
 * Owner and Chief Supervisor are organization-level fields and cannot be changed here.
 *
 * Usage:
 * ```html
 * <app-edit-member-role-modal
 *   [organizationId]="orgId"
 *   [member]="selectedMember"
 *   (close)="onModalClose()"
 *   (roleUpdated)="onRoleUpdated()">
 * </app-edit-member-role-modal>
 * ```
 */
@Component({
  selector: 'app-edit-member-role-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="modal-backdrop fade show" (click)="onClose()"></div>
    <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Edit Member Role</h5>
            <button type="button" class="btn-close" (click)="onClose()" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <!-- Member Info -->
            <div class="mb-4">
              <div class="d-flex align-items-center">
                <div class="entity-avatar entity-avatar-lg fs-4 me-3">
                  <i class="bi bi-person-fill"></i>
                </div>
                <div>
                  <h6 class="mb-0">{{ member.firstName }} {{ member.lastName }}</h6>
                  <small class="text-muted">{{ member.email }}</small>
                </div>
              </div>
            </div>

            <!-- Error Message -->
            <div class="alert alert-danger" *ngIf="errorMessage">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              {{ errorMessage }}
            </div>

            <!-- Success Message -->
            <div class="alert alert-success" *ngIf="successMessage">
              <i class="bi bi-check-circle-fill me-2"></i>
              {{ successMessage }}
            </div>

            <!-- Role Checkboxes -->
            <div class="mb-3">
              <label class="form-label fw-bold">Membership Roles</label>
              <p class="text-muted small mb-3">
                Select the operational roles for this member. At least one role should be selected.
              </p>

              <div class="form-check role-option d-flex align-items-start gap-3 p-3 mb-2 border rounded"
                   [class.border-primary]="isPilot" [class.bg-primary]="isPilot" [class.bg-opacity-10]="isPilot">
                <input
                  class="form-check-input fs-5 ms-0 mt-0 flex-shrink-0 cursor-pointer"
                  type="checkbox"
                  id="rolePilot"
                  [(ngModel)]="isPilot"
                  [disabled]="saving"
                />
                <label class="form-check-label cursor-pointer" [class.text-primary]="isPilot" for="rolePilot">
                  <strong>Pilot</strong>
                  <br>
                  <small class="text-muted">Can operate drones under supervision</small>
                </label>
              </div>

              <div class="form-check role-option d-flex align-items-start gap-3 p-3 mb-2 border rounded"
                   [class.border-primary]="isPIC" [class.bg-primary]="isPIC" [class.bg-opacity-10]="isPIC">
                <input
                  class="form-check-input fs-5 ms-0 mt-0 flex-shrink-0 cursor-pointer"
                  type="checkbox"
                  id="rolePIC"
                  [(ngModel)]="isPIC"
                  [disabled]="saving"
                />
                <label class="form-check-label cursor-pointer" [class.text-primary]="isPIC" for="rolePIC">
                  <strong>Pilot in Command (PIC)</strong>
                  <br>
                  <small class="text-muted">Can supervise operations and sign spray records</small>
                </label>
              </div>
            </div>

            <!-- Organization-Level Roles Info -->
            <div class="alert alert-info" *ngIf="member.isOwner || member.isChiefSupervisor">
              <h6 class="alert-heading">Organization-Level Roles</h6>
              <div *ngIf="member.isOwner">
                <i class="bi bi-star-fill me-2"></i>
                <strong>Owner</strong> - Use "Transfer Ownership" to change
              </div>
              <div *ngIf="member.isChiefSupervisor">
                <i class="bi bi-shield-fill me-2"></i>
                <strong>Chief Supervisor</strong> - Use "Assign Chief Supervisor" to change
              </div>
            </div>

            <!-- Validation Warning -->
            <div class="alert alert-warning" *ngIf="!isPilot && !isPIC">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              At least one role should be selected. Members with no roles will have limited access.
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" (click)="onClose()" [disabled]="saving">
              Cancel
            </button>
            <button type="button" class="btn btn-primary" (click)="onSave()" [disabled]="saving || !hasChanges()">
              <span *ngIf="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ saving ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class EditMemberRoleModalComponent implements OnInit {
  @Input() organizationId!: string;
  @Input() member!: AdminMemberView;
  @Output() close = new EventEmitter<void>();
  @Output() roleUpdated = new EventEmitter<void>();

  isPilot = false;
  isPIC = false;
  saving = false;
  errorMessage = '';
  successMessage = '';

  readonly MembershipRoles = MembershipRoles;

  constructor(private membershipService: OrganizationMembershipService) {}

  ngOnInit(): void {
    // Initialize checkboxes from current role
    this.isPilot = (this.member.role & MembershipRoles.Pilot) === MembershipRoles.Pilot;
    this.isPIC = (this.member.role & MembershipRoles.PilotInCharge) === MembershipRoles.PilotInCharge;
  }

  /**
   * Check if role selection has changed
   */
  hasChanges(): boolean {
    const currentIsPilot = (this.member.role & MembershipRoles.Pilot) === MembershipRoles.Pilot;
    const currentIsPIC = (this.member.role & MembershipRoles.PilotInCharge) === MembershipRoles.PilotInCharge;

    return this.isPilot !== currentIsPilot || this.isPIC !== currentIsPIC;
  }

  /**
   * Save role changes
   */
  async onSave(): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';

    // Calculate new role value
    const newRole = calculateBitwiseRole(this.isPilot, this.isPIC);

    // Validate role change
    const validation = validateRoleChange(this.member.role, newRole);
    if (!validation.valid) {
      this.errorMessage = validation.error || 'Invalid role selection';
      return;
    }

    this.saving = true;

    try {
      await this.membershipService.updateMemberRole(
        this.organizationId,
        this.member.profileId,
        newRole
      );

      this.successMessage = 'Member role updated successfully';

      // Wait briefly to show success message, then emit event and close
      setTimeout(() => {
        this.roleUpdated.emit();
        this.onClose();
      }, 1000);
    } catch (error: any) {
      console.error('Failed to update member role:', error);
      this.errorMessage = error.message || 'Failed to update member role';
      this.saving = false;
    }
  }

  /**
   * Close modal
   */
  onClose(): void {
    this.close.emit();
  }
}
